import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Title, useGetIdentity } from 'react-admin';
import { AdminResponseDtoRoleEnum } from 'shared/api';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { httpClient } from 'shared/services/http-client';
import { getErrorMessage } from 'shared/utils/get-error-message';

type FeedbackStatus = 'pending' | 'answered' | '';

type NamedEntity = {
  id: string;
  name: string;
  email?: string;
};

type VideoOption = NamedEntity & {
  courseId?: string;
};

type FeedbackItem = {
  id: string;
  status: 'pending' | 'answered';
  text: string;
  createdAt: string;
  user: NamedEntity;
  company: NamedEntity | null;
  course: NamedEntity;
  video: NamedEntity;
  curators: NamedEntity[];
  reply: {
    text: string;
    authorId: string;
    authorName: string;
    repliedAt: string;
  } | null;
  isReadByMe?: boolean;
};

type PaginatedFeedback = {
  data: FeedbackItem[];
  total: number;
  page: number;
  count?: number;
  totalPage?: number;
};

type FilterOptions = {
  companies: NamedEntity[];
  courses: NamedEntity[];
  videos: VideoOption[];
  curators: NamedEntity[];
};

type Filters = {
  search: string;
  companyId: string;
  courseId: string;
  videoId: string;
  curatorId: string;
  status: FeedbackStatus;
};

const DEFAULT_FILTERS: Filters = {
  search: '',
  companyId: '',
  courseId: '',
  videoId: '',
  curatorId: '',
  status: '',
};

const EMPTY_FILTER_OPTIONS: FilterOptions = {
  companies: [],
  courses: [],
  videos: [],
  curators: [],
};

const PAGE_SIZE = 20;
const MAX_REPLY_LENGTH = 2000;
const SEARCH_DEBOUNCE_MS = 350;

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusLabel = (status: FeedbackItem['status']) =>
  status === 'answered' ? 'Отвечено' : 'Ожидает ответа';

const loadFeedbackList = async (page: number, filters: Filters) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.courseId) params.set('courseId', filters.courseId);
  if (filters.videoId) params.set('videoId', filters.videoId);
  if (filters.curatorId) params.set('curatorId', filters.curatorId);
  if (filters.status) params.set('status', filters.status);

  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentFeedback}?${params.toString()}`,
  );
  return response.json as PaginatedFeedback;
};

const loadFilterOptions = async () => {
  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentFeedbackFilterOptions}`,
  );
  return response.json as FilterOptions;
};

const loadUnreadCount = async () => {
  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentFeedbackUnreadCount}`,
  );
  return (response.json as { count?: number }).count || 0;
};

const markFeedbackRead = async (id: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentFeedbackRead(id)}`, {
    method: 'POST',
    headers: new Headers({ Accept: 'application/json' }),
  });
};

const replyToFeedback = async (id: string, text: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentFeedbackReply(id)}`, {
    method: 'POST',
    body: JSON.stringify({ text }),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

const ContentFeedback = () => {
  const { identity } = useGetIdentity();
  const role = (identity as { role?: string } | undefined)?.role;
  const canFilterByCurator =
    role === AdminResponseDtoRoleEnum.Superadmin ||
    role === AdminResponseDtoRoleEnum.Admin;

  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(EMPTY_FILTER_OPTIONS);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isReplying, setIsReplying] = useState(false);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const [list, unread] = await Promise.all([
        loadFeedbackList(page, appliedFilters),
        loadUnreadCount(),
      ]);
      if (requestId !== requestIdRef.current) return;
      setItems(list.data || []);
      setTotal(list.total || 0);
      setUnreadCount(unread);
    } catch (e: unknown) {
      if (requestId !== requestIdRef.current) return;
      setError(getErrorMessage(e, 'Ошибка загрузки обратной связи'));
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadFilterOptions()
      .then((options) => {
        setFilterOptionsError(null);
        setFilterOptions({
          companies: options.companies || [],
          courses: options.courses || [],
          videos: options.videos || [],
          curators: options.curators || [],
        });
      })
      .catch((e: unknown) => {
        setFilterOptions(EMPTY_FILTER_OPTIONS);
        setFilterOptionsError(
          getErrorMessage(e, 'Не удалось загрузить опции фильтров'),
        );
      });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setAppliedFilters((current) => {
        if (current.search === filters.search) return current;
        return { ...current, search: filters.search };
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const applyFilterPatch = useCallback((patch: Partial<Filters>) => {
    setFilters((current) => {
      const next = { ...current, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'courseId') && patch.courseId !== current.courseId) {
        next.videoId = '';
      }
      setPage(1);
      setAppliedFilters(next);
      return next;
    });
  }, []);

  const videoOptions = useMemo(() => {
    if (!filters.courseId) return filterOptions.videos;
    return filterOptions.videos.filter((video) => video.courseId === filters.courseId);
  }, [filterOptions.videos, filters.courseId]);

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const hasActiveFilters = useMemo(
    () => Object.values(appliedFilters).some((value) => Boolean(value)),
    [appliedFilters],
  );

  const openItem = async (item: FeedbackItem) => {
    setSelected(item);
    setReplyText('');
    setReplyError(null);
    if (item.isReadByMe === false) {
      try {
        await markFeedbackRead(item.id);
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, isReadByMe: true } : row,
          ),
        );
        setUnreadCount((count) => Math.max(0, count - 1));
      } catch (e: unknown) {
        setError(
          getErrorMessage(e, 'Не удалось отметить сообщение прочитанным'),
        );
      }
    }
  };

  const handleReply = async () => {
    if (!selected) return;
    const text = replyText.trim();
    if (!text) {
      setReplyError('Введите ответ');
      return;
    }
    if (text.length > MAX_REPLY_LENGTH) {
      setReplyError(`Максимум ${MAX_REPLY_LENGTH} символов`);
      return;
    }

    setIsReplying(true);
    setReplyError(null);
    try {
      await replyToFeedback(selected.id, text);
      setSelected(null);
      const options = await loadFilterOptions().catch(() => null);
      if (options) {
        setFilterOptions({
          companies: options.companies || [],
          courses: options.courses || [],
          videos: options.videos || [],
          curators: options.curators || [],
        });
      }
      await refresh();
    } catch (e: unknown) {
      setReplyError(getErrorMessage(e, 'Не удалось отправить ответ'));
    } finally {
      setIsReplying(false);
    }
  };

  return (
    <Box>
      <Title title="Обратная связь" />
      <Card>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Обратная связь по видео</Typography>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <Chip size="small" label="непрочитанные" />
              </Badge>
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {filterOptionsError && (
              <Alert severity="warning">{filterOptionsError}</Alert>
            )}
            {isLoading && <LinearProgress />}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'flex-end' },
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Поиск"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                size="small"
                placeholder="Имя, email, текст, курс, видео"
                sx={{ flex: 1, minWidth: { xs: '100%', md: 220 } }}
              />
              <FormControl
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', md: 160 } }}
              >
                <InputLabel>B2B-клиент</InputLabel>
                <Select
                  label="B2B-клиент"
                  value={filters.companyId}
                  onChange={(e) => applyFilterPatch({ companyId: String(e.target.value) })}
                >
                  <MenuItem value="">Все</MenuItem>
                  {filterOptions.companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', md: 160 } }}
              >
                <InputLabel>Курс</InputLabel>
                <Select
                  label="Курс"
                  value={filters.courseId}
                  onChange={(e) => applyFilterPatch({ courseId: String(e.target.value) })}
                >
                  <MenuItem value="">Все</MenuItem>
                  {filterOptions.courses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', md: 160 } }}
              >
                <InputLabel>Видео</InputLabel>
                <Select
                  label="Видео"
                  value={filters.videoId}
                  onChange={(e) => applyFilterPatch({ videoId: String(e.target.value) })}
                >
                  <MenuItem value="">Все</MenuItem>
                  {videoOptions.map((video) => (
                    <MenuItem key={video.id} value={video.id}>
                      {video.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {canFilterByCurator && (
                <FormControl
                  size="small"
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 160 } }}
                >
                  <InputLabel>Куратор</InputLabel>
                  <Select
                    label="Куратор"
                    value={filters.curatorId}
                    onChange={(e) =>
                      applyFilterPatch({ curatorId: String(e.target.value) })
                    }
                  >
                    <MenuItem value="">Все</MenuItem>
                    {filterOptions.curators.map((curator) => (
                      <MenuItem key={curator.id} value={curator.id}>
                        {curator.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', md: 140 } }}
              >
                <InputLabel>Статус</InputLabel>
                <Select
                  label="Статус"
                  value={filters.status}
                  onChange={(e) =>
                    applyFilterPatch({
                      status: e.target.value as FeedbackStatus,
                    })
                  }
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="pending">Ожидает ответа</MenuItem>
                  <MenuItem value="answered">Отвечено</MenuItem>
                </Select>
              </FormControl>
              {hasActiveFilters && (
                <Button onClick={resetFilters} sx={{ flexShrink: 0 }}>
                  Сбросить
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>B2B-клиент</TableCell>
                    <TableCell>Курс</TableCell>
                    <TableCell>Видео</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={
                        item.isReadByMe === false
                          ? { bgcolor: 'action.hover', fontWeight: 600 }
                          : undefined
                      }
                    >
                      <TableCell>{item.user?.name || '—'}</TableCell>
                      <TableCell>{item.company?.name || '—'}</TableCell>
                      <TableCell>{item.course?.name || '—'}</TableCell>
                      <TableCell>{item.video?.name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={item.status === 'answered' ? 'success' : 'warning'}
                          label={statusLabel(item.status)}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openItem(item)}>
                          Открыть
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          Сообщений пока нет
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={Math.max(0, page - 1)}
              onPageChange={(_, nextPage) => setPage(nextPage + 1)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selected)}
        onClose={() => !isReplying && setSelected(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Обратная связь</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography>
                <strong>Пользователь:</strong> {selected.user?.name || '—'}
              </Typography>
              <Typography>
                <strong>B2B-клиент:</strong> {selected.company?.name || '—'}
              </Typography>
              <Typography>
                <strong>Курс:</strong> {selected.course?.name || '—'}
              </Typography>
              <Typography>
                <strong>Видео:</strong> {selected.video?.name || '—'}
              </Typography>
              <Typography>
                <strong>Кураторы:</strong>{' '}
                {selected.curators?.map((item) => item.name).join(', ') || '—'}
              </Typography>
              <Typography>
                <strong>Дата:</strong> {formatDateTime(selected.createdAt)}
              </Typography>
              <Typography>
                <strong>Статус:</strong> {statusLabel(selected.status)}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selected.text}
              </Box>

              {selected.reply ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'success.light',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Ответ: {selected.reply.authorName} (
                    {formatDateTime(selected.reply.repliedAt)})
                  </Typography>
                  {selected.reply.text}
                </Box>
              ) : (
                <TextField
                  label="Ответ куратора"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  inputProps={{ maxLength: MAX_REPLY_LENGTH }}
                  helperText={`${replyText.trim().length}/${MAX_REPLY_LENGTH}`}
                />
              )}
              {replyError && <Alert severity="error">{replyError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={isReplying}>
            Закрыть
          </Button>
          {selected && !selected.reply && (
            <Button
              variant="contained"
              onClick={handleReply}
              disabled={isReplying}
            >
              Отправить ответ
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentFeedback;
